import { useState, useEffect } from 'react';
import { EducationContent } from '../../utils/educationMap';

interface EducationPanelProps {
  content: EducationContent;
  isQualified: boolean;
  disqualificationReason?: string;
  onContinue: () => void;
  onStartOver: () => void;
  onEditAnswers: () => void;
  isMobile?: boolean;
  showReadyToApply?: boolean;
}

export default function EducationPanel({
  content,
  isQualified,
  disqualificationReason,
  onContinue,
  onStartOver,
  onEditAnswers,
  isMobile = false,
  showReadyToApply = false
}: EducationPanelProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [animatePanel, setAnimatePanel] = useState(false);

  // Animate when scenario changes
  useEffect(() => {
    if (content?.scenarioAnalysis) {
      setAnimatePanel(true);
      const timer = setTimeout(() => setAnimatePanel(false), 400);
      return () => clearTimeout(timer);
    }
  }, [content?.scenarioAnalysis?.title, content?.scenarioAnalysis?.fundingOutlook]);

  const hasContent = content?.scenarioAnalysis || (content?.snapshot && content.snapshot.length > 0);

  if (!hasContent) {
    return (
      <div className="bg-gradient-to-br from-red-50 to-gray-50 rounded-lg shadow-sm border border-red-100 p-6 lg:p-8">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-lightbulb-line text-3xl text-red-600"></i>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Let's Get Started
          </h3>
          <p className="text-gray-600">
            Answer the questions on the left to see personalized funding recommendations 
            that update in real-time based on your complete profile.
          </p>
        </div>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6" data-education-panel>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full px-6 py-4 flex items-center justify-between text-left cursor-pointer"
        >
          <span className="font-semibold text-gray-900">
            <i className="ri-bar-chart-box-line mr-2 text-red-600"></i>
            Your Funding Analysis
          </span>
          <i className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line text-xl text-gray-500`}></i>
        </button>
        
        {isExpanded && (
          <div className="px-6 pb-6">
            <PanelContent
              content={content}
              isQualified={isQualified}
              disqualificationReason={disqualificationReason}
              onContinue={onContinue}
              onStartOver={onStartOver}
              onEditAnswers={onEditAnswers}
              animatePanel={animatePanel}
              showReadyToApply={showReadyToApply}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`bg-gradient-to-br from-red-50 to-gray-50 rounded-lg shadow-sm border border-red-100 p-6 lg:p-8 lg:sticky lg:top-6 transition-all duration-300 ${animatePanel ? 'scale-[1.01] shadow-md' : ''}`} data-education-panel>
      <PanelContent
        content={content}
        isQualified={isQualified}
        disqualificationReason={disqualificationReason}
        onContinue={onContinue}
        onStartOver={onStartOver}
        onEditAnswers={onEditAnswers}
        animatePanel={animatePanel}
        showReadyToApply={showReadyToApply}
      />
    </div>
  );
}

interface PanelContentProps extends Omit<EducationPanelProps, 'isMobile'> {
  animatePanel?: boolean;
}

function PanelContent({
  content,
  isQualified,
  disqualificationReason,
  onContinue,
  onStartOver,
  onEditAnswers,
  animatePanel,
  showReadyToApply
}: PanelContentProps) {
  const { scenarioAnalysis } = content;
  const [isRedirecting, setIsRedirecting] = useState(false);

  const handleExternalRedirect = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setIsRedirecting(true);
    
    // Delay redirect slightly to show the loader
    setTimeout(() => {
      window.open('https://go.mypartner.io/business-financing/?ref=001Qk00000giOrFIAU', '_blank', 'noopener,noreferrer');
      setIsRedirecting(false);
    }, 1500);
  };

  const outlookColors = {
    excellent: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: 'ri-rocket-2-line' },
    good: { bg: 'bg-green-100', text: 'text-green-700', icon: 'ri-thumb-up-line' },
    moderate: { bg: 'bg-amber-100', text: 'text-amber-700', icon: 'ri-scales-3-line' },
    limited: { bg: 'bg-orange-100', text: 'text-orange-700', icon: 'ri-alert-line' }
  };

  const statusColors = {
    positive: { bg: 'bg-emerald-50', border: 'border-emerald-200', icon: 'ri-checkbox-circle-fill text-emerald-500' },
    neutral: { bg: 'bg-gray-50', border: 'border-gray-200', icon: 'ri-checkbox-blank-circle-fill text-gray-400' },
    caution: { bg: 'bg-amber-50', border: 'border-amber-200', icon: 'ri-error-warning-fill text-amber-500' }
  };

  const fitColors = {
    best: { bg: 'bg-emerald-500', text: 'Best Fit' },
    good: { bg: 'bg-green-500', text: 'Good Fit' },
    possible: { bg: 'bg-gray-400', text: 'Possible' }
  };

  return (
    <>
      {/* Scenario Analysis - Combined View */}
      {scenarioAnalysis && (
        <div className="space-y-5">
          {/* Header with Title & Outlook */}
          <div>
            <div className="flex items-start justify-between gap-3 mb-3">
              <h3 className="text-lg font-bold text-gray-900 leading-tight">
                {scenarioAnalysis.title}
              </h3>
              <div className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-semibold ${outlookColors[scenarioAnalysis.fundingOutlook].bg} ${outlookColors[scenarioAnalysis.fundingOutlook].text}`}>
                <i className={`${outlookColors[scenarioAnalysis.fundingOutlook].icon} mr-1`}></i>
                {scenarioAnalysis.fundingOutlook.charAt(0).toUpperCase() + scenarioAnalysis.fundingOutlook.slice(1)}
              </div>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">
              {scenarioAnalysis.summary}
            </p>
          </div>

          {/* Estimated Range & Approval */}
          {(scenarioAnalysis.estimatedRange || scenarioAnalysis.approvalLikelihood) && (
            <div className="grid grid-cols-2 gap-3">
              {scenarioAnalysis.estimatedRange && (
                <div className="bg-white rounded-lg p-3 border border-gray-100">
                  <div className="text-xs text-gray-500 mb-1">Est. Funding Range</div>
                  <div className="text-sm font-bold text-gray-900">
                    {scenarioAnalysis.estimatedRange.min} - {scenarioAnalysis.estimatedRange.max}
                  </div>
                </div>
              )}
              {scenarioAnalysis.approvalLikelihood && (
                <div className="bg-white rounded-lg p-3 border border-gray-100">
                  <div className="text-xs text-gray-500 mb-1">Approval Likelihood</div>
                  <div className="text-sm font-bold text-gray-900">
                    {scenarioAnalysis.approvalLikelihood}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Key Factors */}
          {scenarioAnalysis.keyFactors.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Key Factors
              </h4>
              <div className="space-y-2">
                {scenarioAnalysis.keyFactors.map((factor, index) => (
                  <div 
                    key={index} 
                    className={`flex items-start gap-2 p-2 rounded-lg border ${statusColors[factor.status].bg} ${statusColors[factor.status].border}`}
                  >
                    <i className={`${statusColors[factor.status].icon} mt-0.5 flex-shrink-0`}></i>
                    <div className="min-w-0">
                      <div className="text-xs font-semibold text-gray-700">{factor.label}</div>
                      <div className="text-xs text-gray-600">{factor.detail}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommended Products */}
          {scenarioAnalysis.recommendedProducts.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Recommended Products
              </h4>
              <div className="space-y-2">
                {scenarioAnalysis.recommendedProducts.map((product, index) => (
                  <div key={index} className="bg-white rounded-lg p-3 border border-gray-100">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-semibold text-gray-900">{product.name}</span>
                      <span className={`text-[10px] font-bold text-white px-2 py-0.5 rounded-full ${fitColors[product.fit].bg}`}>
                        {fitColors[product.fit].text}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600">{product.reason}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tips */}
          {scenarioAnalysis.tips.length > 0 && (
            <div className="bg-white rounded-lg p-4 border border-red-100">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center">
                <i className="ri-lightbulb-line text-red-500 mr-1"></i>
                Tips for Your Scenario
              </h4>
              <ul className="space-y-1.5">
                {scenarioAnalysis.tips.map((tip, index) => (
                  <li key={index} className="flex items-start gap-2 text-xs text-gray-700">
                    <i className="ri-arrow-right-s-line text-red-500 mt-0.5 flex-shrink-0"></i>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Fallback: What This Means (for early stages before scenario builds) */}
      {!scenarioAnalysis && content.whatThisMeans && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
            <i className="ri-lightbulb-line mr-2 text-red-600"></i>
            Getting Started
          </h3>
          <p className="text-sm text-gray-700 leading-relaxed">
            {content.whatThisMeans}
          </p>
        </div>
      )}

      {/* Disqualification Message */}
      {!isQualified && disqualificationReason && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-5">
          <div className="flex items-start gap-3">
            <i className="ri-error-warning-line text-2xl text-red-500 flex-shrink-0"></i>
            <div>
              <h4 className="font-semibold text-red-900 mb-1">
                Currently Not Eligible
              </h4>
              <p className="text-sm text-red-700 mb-3">
                {disqualificationReason}
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={onEditAnswers}
                  className="px-4 py-2 bg-white border border-red-300 text-red-700 rounded-lg text-sm font-medium hover:bg-red-50 transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-edit-line mr-1"></i>
                  Edit Answers
                </button>
                <button
                  onClick={onStartOver}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-restart-line mr-1"></i>
                  Start Over
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Next Steps - Only show when showReadyToApply is true */}
      {content.nextSteps && isQualified && showReadyToApply && (
        <div 
          data-ready-to-apply
          className="bg-white rounded-lg p-4 border-2 border-red-400 mt-5 shadow-lg animate-pulse-glow"
          style={{
            animation: 'glowPulse 2s ease-in-out infinite',
            boxShadow: '0 0 20px rgba(220, 38, 38, 0.4), 0 0 40px rgba(220, 38, 38, 0.2)'
          }}
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
            <i className="ri-rocket-line mr-2 text-red-600"></i>
            Ready to Apply
          </h3>
          <p className="text-sm text-gray-700 mb-4">
            {content.nextSteps}
          </p>
          <a
            href="https://go.mypartner.io/business-financing/?ref=001Qk00000giOrFIAU"
            onClick={handleExternalRedirect}
            className="block w-full px-6 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-all whitespace-nowrap cursor-pointer text-center"
          >
            {isRedirecting ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Transferring...
              </span>
            ) : (
              <>
                Continue to Full Application
                <i className="ri-arrow-right-line ml-2"></i>
              </>
            )}
          </a>
        </div>
      )}

      {/* Full-screen Loading Overlay */}
      {isRedirecting && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-8 shadow-2xl max-w-sm mx-4 text-center">
            <div className="w-16 h-16 mx-auto mb-4 relative">
              <div className="absolute inset-0 border-4 border-red-200 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-red-600 rounded-full border-t-transparent animate-spin"></div>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Transferring to Application
            </h3>
            <p className="text-gray-600 text-sm">
              Please wait while we redirect you to our secure application portal...
            </p>
            <div className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-500">
              <i className="ri-shield-check-line text-green-600"></i>
              <span>Secure Connection</span>
            </div>
          </div>
        </div>
      )}

      {/* Glow animation styles */}
      <style>{`
        @keyframes glowPulse {
          0%, 100% {
            box-shadow: 0 0 20px rgba(220, 38, 38, 0.4), 0 0 40px rgba(220, 38, 38, 0.2);
          }
          50% {
            box-shadow: 0 0 30px rgba(220, 38, 38, 0.6), 0 0 60px rgba(220, 38, 38, 0.3);
          }
        }
      `}</style>
    </>
  );
}
